import Alpine from 'alpinejs';

interface GroupDef {
    id: string;
    label: string;
}

interface QuizCategorizationState {
    ready: boolean;
    formData: Record<string, boolean>;
    validated: boolean;
    activeTab: number;
    totalTabs: number;
    validatedTabs: number[];
    allFields: string[][];
    correctFields: string[][];
    quizId: string;
    passPercentage: number;
    hasGroups: boolean;
    groupSelectorCompleted: boolean;
    groups: GroupDef[];
    categoryGroupIds: (string | null)[];
    visibleTabs: number[];
    $el: HTMLElement;
    $nextTick(callback: () => void): void;
    init: () => void;
    hasSelection: () => boolean;
    computeVisibleTabs: () => void;
    currentVisibleIndex: () => number;
    visibleTabCount: () => number;
    next: () => void;
    back: () => void;
    getCorrectCount: () => number;
    isSuccess: () => boolean;
    isLastTabValidated: () => boolean;
    isLastTab: () => boolean;
    showResultsButton: () => boolean;
    restart: () => void;
}

export function registerQuizCategorization(): void {
    Alpine.data('quizCategorization', function (this: QuizCategorizationState, totalTabs: number) { return {
        ready: false,
        formData: {} as Record<string, boolean>,
        validated: false,
        activeTab: 0,
        totalTabs,
        validatedTabs: [] as number[],
        allFields: [] as string[][],
        correctFields: [] as string[][],
        quizId: '',
        passPercentage: 100,
        hasGroups: false,
        groupSelectorCompleted: false,
        groups: [] as GroupDef[],
        categoryGroupIds: [] as (string | null)[],
        visibleTabs: [] as number[],

        init() {
            this.allFields = JSON.parse(this.$el.dataset.allFields || '[]');
            this.correctFields = JSON.parse(this.$el.dataset.correctFields || '[]');

            const formEl = this.$el.closest('[data-schema-event]') as HTMLElement | null;
            this.quizId = formEl?.dataset.schemaEvent || '';
            this.passPercentage = parseInt(formEl?.dataset.passPercentage || '100', 10);

            this.groups = JSON.parse(this.$el.dataset.groups || '[]');
            this.categoryGroupIds = JSON.parse(this.$el.dataset.categoryGroupIds || '[]');
            this.hasGroups = this.groups.length > 0;

            if (this.hasGroups) {
                this.activeTab = -1;
            }

            this.$nextTick(() => {
                this.ready = true;
            });
            window.addEventListener('quiz-restart', (e: Event) => {
                const detail = (e as CustomEvent).detail;
                if (detail?.quizId === this.quizId) {
                    this.restart();
                }
            });
        },

        computeVisibleTabs() {
            const selectedGroupIds: string[] = [];
            for (const group of this.groups) {
                if (this.formData[`_group_${group.id}`] === true) {
                    selectedGroupIds.push(group.id);
                }
            }

            this.visibleTabs = [];
            for (let i = 0; i < this.categoryGroupIds.length; i++) {
                const groupId = this.categoryGroupIds[i];
                if (groupId !== null && selectedGroupIds.includes(groupId)) {
                    this.visibleTabs.push(i);
                }
            }
        },

        currentVisibleIndex(): number {
            return this.visibleTabs.indexOf(this.activeTab);
        },

        visibleTabCount(): number {
            return this.visibleTabs.length;
        },

        hasSelection(): boolean {
            if (this.hasGroups && !this.groupSelectorCompleted) {
                return this.groups.some(
                    group => this.formData[`_group_${group.id}`] === true
                );
            }
            const fields = this.allFields[this.activeTab] || [];
            return fields.some(field => this.formData[field] === true);
        },

        isLastTabValidated(): boolean {
            if (this.hasGroups && this.visibleTabs.length > 0) {
                const lastVisible = this.visibleTabs[this.visibleTabs.length - 1];
                return this.validatedTabs.includes(lastVisible);
            }
            return this.validatedTabs.includes(this.totalTabs - 1);
        },

        isLastTab(): boolean {
            if (this.hasGroups && this.visibleTabs.length > 0) {
                const lastVisible = this.visibleTabs[this.visibleTabs.length - 1];
                return this.activeTab === lastVisible;
            }
            return this.activeTab === this.totalTabs - 1;
        },

        showResultsButton(): boolean {
            return this.isLastTab() && this.validated;
        },

        getCorrectCount(): number {
            let correct = 0;
            const tabsToCheck = this.hasGroups ? this.visibleTabs : Array.from({ length: this.totalTabs }, (_, i) => i);

            tabsToCheck.forEach(index => {
                const correctForTab = this.correctFields[index] || [];
                const allFieldsForTab = this.allFields[index] || [];
                const wrongFields = allFieldsForTab.filter(field => !correctForTab.includes(field));

                const hasCorrectSelection = correctForTab.every(field => this.formData[field] === true);
                const hasWrongSelection = wrongFields.some(field => this.formData[field] === true);

                if (hasCorrectSelection && !hasWrongSelection) {
                    correct++;
                }
            });
            return correct;
        },

        isSuccess(): boolean {
            const totalQuestions = this.hasGroups ? this.visibleTabs.length : this.totalTabs;
            return (this.getCorrectCount() / totalQuestions) * 100 >= this.passPercentage;
        },

        next(): void {
            if (this.hasGroups && !this.groupSelectorCompleted) {
                this.computeVisibleTabs();
                if (this.visibleTabs.length === 0) {return;}
                this.groupSelectorCompleted = true;
                this.activeTab = this.visibleTabs[0];
                this.validated = false;
                return;
            }

            if (!this.validatedTabs.includes(this.activeTab)) {
                this.validatedTabs.push(this.activeTab);
                this.validated = true;
            } else {
                if (this.hasGroups) {
                    const currentIdx = this.currentVisibleIndex();
                    if (currentIdx < this.visibleTabs.length - 1) {
                        this.activeTab = this.visibleTabs[currentIdx + 1];
                        this.validated = this.validatedTabs.includes(this.activeTab);
                    } else if (this.isLastTabValidated()) {
                        window.dispatchEvent(new CustomEvent('quiz-complete', {
                            detail: {
                                quizId: this.quizId,
                                success: this.isSuccess(),
                                correctCount: this.getCorrectCount(),
                                totalQuestions: this.visibleTabs.length,
                            }
                        }));
                    }
                } else {
                    if (this.activeTab < this.totalTabs - 1) {
                        this.activeTab++;
                        this.validated = this.validatedTabs.includes(this.activeTab);
                    } else if (this.isLastTabValidated()) {
                        window.dispatchEvent(new CustomEvent('quiz-complete', {
                            detail: {
                                quizId: this.quizId,
                                success: this.isSuccess(),
                                correctCount: this.getCorrectCount(),
                                totalQuestions: this.totalTabs,
                            }
                        }));
                    }
                }
            }
        },

        back(): void {
            if (this.hasGroups) {
                const currentIdx = this.currentVisibleIndex();
                if (currentIdx > 0) {
                    this.activeTab = this.visibleTabs[currentIdx - 1];
                    this.validated = this.validatedTabs.includes(this.activeTab);
                }
                return;
            }
            if (this.activeTab > 0) {
                this.activeTab--;
                this.validated = this.validatedTabs.includes(this.activeTab);
            }
        },

        restart(): void {
            this.formData = {};
            this.validated = false;
            this.activeTab = this.hasGroups ? -1 : 0;
            this.validatedTabs = [];
            this.groupSelectorCompleted = false;
            this.visibleTabs = [];
        }
    };});
}
